import { css } from '@emotion/react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'components/field/Button';
import CopyComponent from 'components/field/CopyComponent';
import CreditCard from 'components/field/CreditCard';
import FormItem from 'components/form/FormItem';
import Accordion from 'components/global/Accordion';
import Alert from 'components/global/Alert';
import Collapse from 'components/global/Collapse';
import Icon from 'components/global/Icon';
import Loading from 'components/global/Loading';
import Radio from 'components/global/Radio';
import Typography from 'components/global/Typography';
import { ModalActions } from 'components/global/styled/Modal.styled';
import PaytrIframe from 'components/payment/paytr/PaytrIframe';
import CompleteRenewSubscriptionTapPaymentButton from 'components/payment/tap/CompleteRenewSubscriptionTapPaymentButton';
import CompleteSubscriptionTapPaymentButton from 'components/payment/tap/CompleteSubscriptionTapPaymentButton';
import TapIframe from 'components/payment/tap/TapIframe';

import useAlert from 'hooks/useAlert';
import useLocale from 'hooks/useLocale';

import { setLoading } from 'stores/layoutSlice';
import { getClientCards } from 'stores/userSlice';

import { PAYMENT_GATEWAY_TYPE, PAYMENT_METHODS, TEST_CARDS } from 'utils/constants';

import { faAngleLeft, faBadgeCheck } from '@fortawesome/pro-solid-svg-icons';

import { setPaymentResult } from '../SubscriptionAccountSlice';
import * as S from '../SubscriptonAccountModal.styled';
import MonthlyFreeIntegratorShipments from './MonthlyFreeIntegratorShipments';

export default function PaymentModal({
	closeModal,
	prevStep,
	nextStep,
	subscription,
	currentPackage,
	selectedPackage,
	planType,
	renewSubscription
}) {
	// Hooks and states declaration
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const { showError } = useAlert();
	const { Currency, getLocaleDateWithTime } = useLocale();

	const { subscriptionDiscountedAmount, subscriptionRetryPaymentAmount } = useSelector(
		(state) => state.subscriptionAccount
	);
	const {
		localization: { paymentGatewayType },
		clientCards,
		accountSuspended,
		subscriptionPaymentType
	} = useSelector((state) => state.user);

	const [isIframeLoaded, setIsIframeLoaded] = useState(false);
	const [creditInputsValid, setCreditInputsValid] = useState(false);
	const [selectedRadio, setSelectedRadio] = useState('');
	const [selectedSavedCard, setSelectedSavedCard] = useState(null);

	const hasPayTRCardValidation = useMemo(
		() => paymentGatewayType === PAYMENT_GATEWAY_TYPE.PAYTR && selectedRadio === PAYMENT_METHODS.CREDIT_CARD,
		[paymentGatewayType, selectedRadio]
	);

	const isCreditCardTapPayment = useMemo(
		() =>
			[PAYMENT_GATEWAY_TYPE.TAP_PAYMENT, PAYMENT_GATEWAY_TYPE.TAP_PAYMENT_UAE].includes(paymentGatewayType) &&
			selectedRadio === PAYMENT_METHODS.CREDIT_CARD,
		[paymentGatewayType, selectedRadio]
	);

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					cardHolderName: yup
						.string()
						.nullable()
						.when([], {
							is: () => hasPayTRCardValidation,
							then: (schema) => schema.required(),
							otherwise: (schema) => schema
						}),
					cardNumber: yup
						.string()
						.nullable()
						.min(16)
						.max(20)
						.transform((value) => value?.replace(/\s/g, ''))
						.when([], {
							is: () => hasPayTRCardValidation,
							then: (schema) => schema.required(),
							otherwise: (schema) => schema
						}),
					cvv: yup
						.string()
						.nullable()
						.min(3)
						.max(3)
						.when([], {
							is: () => hasPayTRCardValidation,
							then: (schema) => schema.required(),
							otherwise: (schema) => schema
						}),
					cardExpiry: yup
						.string()
						.nullable()
						.matches(/^(0[1-9]|1[0-2])\/[2-9]\d$/g, t('validation.invalidExpiryDate'))
						.when([], {
							is: () => hasPayTRCardValidation,
							then: (schema) => schema.required(),
							otherwise: (schema) => schema
						})
				})
				.required(),
		[hasPayTRCardValidation]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			cardHolderName: null,
			cardNumber: null,
			cvv: null,
			cardExpiry: null
		}
	});

	useEffect(() => {
		if (paymentGatewayType === PAYMENT_GATEWAY_TYPE.PAYTR) {
			setIsIframeLoaded(true);
		}
	}, [paymentGatewayType]);

	useEffect(() => {
		if (selectedRadio === PAYMENT_METHODS.SAVED) {
			setSelectedSavedCard({
				card: clientCards?.[0]?.ID,
				cvvRequired: clientCards?.[0]?.requireCVV
			});
		}
	}, [clientCards, selectedRadio]);

	useEffect(() => {
		getClientCardsOfCompany();
	}, []);

	const radioChange = ({ currentTarget: { id } }) => {
		setSelectedSavedCard(null);
		setSelectedRadio(id);
	};

	const savedCardRadioChange = (id, requireCVV) => {
		setSelectedSavedCard({ card: Number(id), cvvRequired: requireCVV });
	};

	const getClientCardsOfCompany = async () => {
		if (renewSubscription) {
			setSelectedRadio(PAYMENT_METHODS.CREDIT_CARD);
			return;
		}
		dispatch(setLoading(true));

		dispatch(getClientCards({}))
			.unwrap()
			.then((result) => {
				if (result.length > 0) {
					setSelectedRadio(PAYMENT_METHODS.SAVED);
				} else {
					setSelectedRadio(PAYMENT_METHODS.CREDIT_CARD);
				}
			})
			.catch(() => {
				setSelectedRadio(PAYMENT_METHODS.CREDIT_CARD);
			})
			.finally(() => dispatch(setLoading(false)));
	};

	const onPaymentResult = async (result) => {
		dispatch(setPaymentResult(result));
		if (result?.status === 'paid') {
			dispatch(getClientCards({ forceSync: true }));
			nextStep();
		} else if (result?.status === 'failed') {
			showError({
				header: 'error',
				message: result?.description
			});
		}
	};

	return (
		<>
			<S.ModalContentWrapper>
				<FormProvider {...formMethods}>
					{selectedPackage &&
						(accountSuspended ||
							currentPackage.planName !== selectedPackage.planName ||
							planType.toUpperCase() !== subscriptionPaymentType) && (
							<S.PriceContainer>
								<FormItem>
									<Typography fontWeight="500" color="var(--content-3)">
										{t('changeSubscriptionTitle', {
											selectedPackage: t(`${selectedPackage.planName}.packageTitle`),
											currentPackage: t(`${currentPackage.planName}.packageTitle`)
										})}
									</Typography>

									<div
										className="mt-3"
										style={{
											display: 'flex'
										}}>
										{(planType === 'yearly'
											? selectedPackage.yearlyPriceWithTax
											: selectedPackage.monthlyPriceWithTax) ===
											subscriptionDiscountedAmount?.amount && (
											<S.PricePadding fontWeight="bold">
												{t('standardPriceInformation', {
													price: subscriptionDiscountedAmount.amount?.toFixed(2),
													currency: subscriptionDiscountedAmount.currency,
													planType: planType === 'yearly' ? t('year') : t('month')
												})}
											</S.PricePadding>
										)}

										{(planType === 'yearly'
											? selectedPackage.yearlyPriceWithTax
											: selectedPackage.monthlyPriceWithTax) >
											subscriptionDiscountedAmount?.amount &&
											subscriptionDiscountedAmount.amount !== 1 && (
												<S.PricePadding fontWeight="bold">
													{t('discountedPriceInformation', {
														price: subscriptionDiscountedAmount.amount?.toFixed(2),
														currency: subscriptionDiscountedAmount.currency,
														planType: planType === 'yearly' ? t('year') : t('month'),
														discount: subscriptionDiscountedAmount.unusedPackageAmount
													})}
												</S.PricePadding>
											)}

										{subscriptionDiscountedAmount?.amount === 1 &&
											!subscriptionDiscountedAmount.nextBillingDateForPromoCode && (
												<S.PricePadding fontWeight="bold">
													{t('paidPackagePriceInformation', {
														price: subscriptionDiscountedAmount.amount?.toFixed(2),
														currency: subscriptionDiscountedAmount.currency,
														nextBillingDate: getLocaleDateWithTime(
															subscriptionDiscountedAmount?.nextBillingDate
														),
														planType: planType === 'yearly' ? t('year') : t('month')
													})}
												</S.PricePadding>
											)}

										{subscriptionDiscountedAmount?.amount === 1 &&
											subscriptionDiscountedAmount.nextBillingDateForPromoCode && (
												<S.PricePadding fontWeight="bold">
													{t('promoCodeInformation', {
														price: subscriptionDiscountedAmount.amount?.toFixed(2),
														currency: subscriptionDiscountedAmount.currency,
														nextBillingDateForPromoCode: getLocaleDateWithTime(
															subscriptionDiscountedAmount?.nextBillingDateForPromoCode
														)
													})}
												</S.PricePadding>
											)}
										{(planType === 'yearly'
											? selectedPackage.yearlyPriceWithTax
											: selectedPackage.monthlyPriceWithTax) ===
											subscriptionDiscountedAmount?.amount && (
											<>
												<Typography
													style={{
														textDecoration: 'line-through',
														alignSelf: 'center'
													}}
													color="var(--content-3)"
													fontWeight="500">
													{planType === 'yearly'
														? selectedPackage.yearlyPriceWithoutDiscount
														: selectedPackage.monthlyPriceWithoutDiscount}{' '}
													{planType === 'yearly' &&
														selectedPackage.yearlyPriceWithoutDiscount &&
														Currency}
													{planType === 'monthly' &&
														selectedPackage.monthlyPriceWithoutDiscount &&
														Currency}
												</Typography>

												{((planType === 'yearly' && selectedPackage.yearlyDiscountRate) ||
													(planType === 'monthly' &&
														selectedPackage.monthlyDiscountRate)) && (
													<S.DiscountBadge>
														<Icon style={{ color: 'var(--green)' }} icon={faBadgeCheck} />
														<Typography color="var(--green)" fontWeight="700">
															{t('upgradeAccount.save')}{' '}
															{planType === 'yearly' &&
																selectedPackage.yearlyDiscountRate &&
																parseFloat(
																	selectedPackage.yearlyDiscountRate?.toFixed(0)
																)}
															{planType === 'monthly' &&
																selectedPackage.monthlyDiscountRate &&
																parseFloat(
																	selectedPackage.monthlyDiscountRate?.toFixed(0)
																)}
															%
														</Typography>
													</S.DiscountBadge>
												)}
											</>
										)}
									</div>
									{selectedPackage.monthlyFreeIntegratorOrder && (
										<MonthlyFreeIntegratorShipments pricingPackage={selectedPackage} />
									)}
								</FormItem>
							</S.PriceContainer>
						)}

					{renewSubscription && (
						<S.PriceContainer>
							<FormItem>
								<Typography className="mb-3" fontWeight="500" color="var(--content-3)">
									renewSubscription
								</Typography>

								<Typography variant="p2" fontWeight="500">
									{t('renewSubscriptionInfoText', {
										price: subscriptionRetryPaymentAmount?.amount?.toFixed(2),
										currency: subscriptionRetryPaymentAmount?.currency
									})}
								</Typography>
							</FormItem>
						</S.PriceContainer>
					)}

					{clientCards?.length > 0 && (
						<>
							<S.PaymentCard id={PAYMENT_METHODS.SAVED} onClick={radioChange}>
								<Radio checked={selectedRadio === PAYMENT_METHODS.SAVED} readOnly />
								<Typography variant="p1" component="span" fontWeight={500}>
									savedPaymentMethod
								</Typography>
							</S.PaymentCard>

							<Collapse visible={selectedRadio === PAYMENT_METHODS.SAVED} invisibleMode defaultVisible>
								<S.UserCardsContainer>
									{clientCards?.map((card, index) => (
										<CreditCard
											key={index}
											onClick={() => savedCardRadioChange(card.ID, card?.requireCVV)}
											lastFour={card.cardLastFour}
											cardBrand={card.cardBrand}
											year={card.year}
											month={card.month}
											isSelected={selectedSavedCard?.card === card.ID}
										/>
									))}
								</S.UserCardsContainer>
							</Collapse>
						</>
					)}

					<S.PaymentCard id={PAYMENT_METHODS.CREDIT_CARD} onClick={radioChange}>
						<Radio checked={selectedRadio === PAYMENT_METHODS.CREDIT_CARD} readOnly />
						<Typography variant="p1" component="span" fontWeight={500}>
							creditcard
						</Typography>
					</S.PaymentCard>
					<Collapse visible={selectedRadio === PAYMENT_METHODS.CREDIT_CARD} invisibleMode defaultVisible>
						{!isIframeLoaded && <Loading />}
						{[PAYMENT_GATEWAY_TYPE.TAP_PAYMENT_UAE, PAYMENT_GATEWAY_TYPE.TAP_PAYMENT].includes(
							paymentGatewayType
						) && (
							<S.CreditCardIFrameContainer>
								<TapIframe
									subscription={true}
									setIsIframeLoaded={setIsIframeLoaded}
									setInputsValid={(isValid) => setCreditInputsValid(isValid)}
								/>
							</S.CreditCardIFrameContainer>
						)}

						{paymentGatewayType === PAYMENT_GATEWAY_TYPE.PAYTR && (
							<S.CreditCardContainer>
								<PaytrIframe subscription={true} />
							</S.CreditCardContainer>
						)}
						{import.meta.env.VITE_SANDBOX === 'true' && (
							<S.CreditCardIFrameContainer className="mt-4">
								<Alert content="sandboxArea" showIcon variant="info" />
								<Accordion header="testCards" className="mt-3">
									<S.TestCardContainer>
										{TEST_CARDS?.[paymentGatewayType]?.map((card, index) => (
											<S.TestCard key={index}>
												<Typography fontWeight={600} className="pb-3">
													<Trans
														i18nKey="chargeAccount.testCard"
														values={{
															count: index + 1
														}}
													/>
												</Typography>
												<S.TestCardRow>
													<Typography fontWeight={500}>cardNumber</Typography>
													<CopyComponent value={card.cardNumber} className="copy-area" />
												</S.TestCardRow>
												<S.TestCardRow>
													<Typography fontWeight={500}>expiry</Typography>
													<CopyComponent
														value={`${card.expiryMonth}/${card.expiryYear}`}
														className="copy-area"
													/>
												</S.TestCardRow>
												<S.TestCardRow>
													<Typography fontWeight={500}>cvv</Typography>
													<CopyComponent value={card.cvv} className="copy-area" />
												</S.TestCardRow>
												<S.TestCardRow>
													<Typography fontWeight={500}>cardHolderName</Typography>
													<CopyComponent value={card.cardHolderName} className="copy-area" />
												</S.TestCardRow>
											</S.TestCard>
										))}
									</S.TestCardContainer>
								</Accordion>
							</S.CreditCardIFrameContainer>
						)}
					</Collapse>
				</FormProvider>
			</S.ModalContentWrapper>

			<FormProvider {...formMethods}>
				<ModalActions>
					<Button
						variant="blackOutline"
						css={css`
							text-decoration: underline;
							border: none !important;
						`}
						label="cancel"
						onClick={closeModal}
					/>
					{!renewSubscription && (
						<Button
							variant="outline"
							icon={faAngleLeft}
							label="previous"
							onClick={() => {
								prevStep();
							}}
						/>
					)}
					{[
						PAYMENT_GATEWAY_TYPE.TAP_PAYMENT,
						PAYMENT_GATEWAY_TYPE.TAP_PAYMENT_UAE,
						PAYMENT_GATEWAY_TYPE.PAYTR
					].includes(paymentGatewayType) && (
						<>
							{subscription && (
								<CompleteSubscriptionTapPaymentButton
									selectedSavedCard={selectedSavedCard}
									selectedPackage={selectedPackage}
									planType={planType}
									onPaymentResult={onPaymentResult}
									isPayTR={paymentGatewayType === PAYMENT_GATEWAY_TYPE.PAYTR}
									disabled={isCreditCardTapPayment ? !creditInputsValid : false}
								/>
							)}

							{renewSubscription && (
								<CompleteRenewSubscriptionTapPaymentButton
									selectedSavedCard={selectedSavedCard}
									onPaymentResult={onPaymentResult}
									isPayTR={paymentGatewayType === PAYMENT_GATEWAY_TYPE.PAYTR}
									disabled={isCreditCardTapPayment ? !creditInputsValid : false}
								/>
							)}
						</>
					)}
				</ModalActions>
			</FormProvider>
		</>
	);
}
